<template>
    <div id="manage-settings" class="relative h-full flex flex-col h-full p-4">
        <div class="flex items-center h-10">
            <h1 class="m-0 truncate">{{ $t('manage_school_settings_header') }}</h1>
        </div>
        <div id="settings-section" class="flex-1 flex flex-col overflow-hidden">
            <div class="settings-school-name-section flex flex-col md:flex-row">
                <div class="settings-school-name-form flex-1 flex flex-col md:flex-row">
                    <div class="form-control flex flex-col w-full">
                        <h3 class="m-0">{{ $t('manage_school_settings_label_school_name') }}</h3>
                        <div class="flex items-center">
                            <input
                                v-model="schoolName"
                                v-validate="'required'"
                                @input="generateSlug()"
                                type="text"
                                name="school-name"
                                class="school-name-input flex-1"
                            />
                        </div>
                        <span v-if="errors.has('school-name')" class="block font-size-16 text-red">
                            <i class="fas fa-exclamation-circle"></i>
                            {{ $t('manage_school_settings_school_name_invalid') }}
                        </span>
                    </div>
                    <div class="form-control flex flex-col w-full">
                        <h3 class="m-0">{{ $t('manage_school_settings_label_school_slug') }}</h3>
                        <div class="flex items-center">
                            <input
                                v-model="schoolSlug"
                                v-validate="'required'"
                                type="text"
                                name="school-slug"
                                class="school-slug-input flex-1"
                            />
                        </div>
                        <span v-if="errors.has('school-slug') || invalidSlug" class="block font-size-16 text-red">
                            <i class="fas fa-exclamation-circle"></i>
                            {{
                                invalidSlug
                                    ? $t('manage_school_settings_school_slug_invalid_syntax')
                                    : $t('manage_school_settings_school_name_invalid')
                            }}
                        </span>
                    </div>
                </div>
                <div class="save-button-box flex md:items-end w-full md:w-auto">
                    <XButton
                        @click="saveSchool()"
                        :disabled="disableAll"
                        class="school-name-button w-full shadow-text-none"
                        bg-color="#17a2b8"
                    >
                        <i class="fas fa-save"></i>
                        <span class="hidden md:inline">{{ $t('manage_school_settings_button_save') }}</span>
                    </XButton>
                </div>
            </div>
            <div class="form-control flex flex-col w-full">
                <h3 class="m-0">{{ $t('manage_school_settings_label_school_link') }}</h3>
                <div class="flex items-center">
                    <input
                        v-validate="'required'"
                        :value="`${nuxtPath}/${you.provider === 'feide' ? 'skoler' : 'school'}/${schoolSlug}`"
                        type="text"
                        name="school-slug"
                        class="school-url-input flex-1"
                        readonly
                    />
                    <XButton
                        @click="openSchool"
                        :disabled="disableAll"
                        class="school-url-button w-auto shadow-text-none"
                        bg-color="#28a745"
                    >
                        <i class="fas fa-external-link-alt"></i>
                        <span class="hidden md:inline">{{ $t('manage_school_settings_button_open') }}</span>
                    </XButton>
                </div>
                <span v-if="errors.has('school-slug') || invalidSlug" class="block font-size-16 text-red">
                    <i class="fas fa-exclamation-circle"></i>
                    {{
                        invalidSlug
                            ? $t('manage_school_settings_school_slug_invalid_syntax')
                            : $t('manage_school_settings_school_name_invalid')
                    }}
                </span>
            </div>
            <div class="setting-admin-section flex-1 flex flex-col overflow-hidden">
                <div class="flex justify-between items-center">
                    <h3 class="m-0">{{ $t('manage_school_settings_header_admin') }}</h3>
                    <XButton
                        @click="showManageAssignAdmin = true"
                        :disabled="disableAll || loading"
                        class="shadow-text-none"
                        bg-color="#28a745"
                    >
                        <i class="fas fa-plus"></i>
                        <span class="hidden md:inline">{{ $t('manage_school_settings_button_assign_admin') }}</span>
                    </XButton>
                </div>
                <ManageSearch @search="onSearch($event)" :disabled-search="disableAll || loading" />
                <ManageTable
                    @pagination-change="onPageChange"
                    :list="users"
                    :current-page="currentPage"
                    :page-count="pageCount"
                    :items-per-page="itemsPerPage"
                    :total-items="totalItems"
                    :loading="loading"
                    :disable-pagination="disableAll"
                >
                    <template #header>
                        <h3 class="w-2/5 m-0 font-size-16">{{ $t('manage_school_table_header_name') }}</h3>
                        <h3 class="w-1/5 m-0 font-size-16 text-center">
                            {{ $t('manage_school_users_table_header_roles') }}
                        </h3>
                        <h3 class="w-1/5 m-0 font-size-16 text-center">
                            {{ $t('manage_school_users_table_header_status') }}
                        </h3>
                        <h3 class="w-1/5 m-0 font-size-16 text-right">
                            {{ $t('manage_school_users_table_header_actions') }}
                        </h3>
                    </template>
                    <template v-slot="{ item }">
                        <div class="user-detail flex items-center w-full xl:w-2/5">
                            <div class="avatar flex justify-center items-center rounded-full overflow-hidden">
                                <img
                                    v-if="item.image_profile"
                                    :src="item.image_profile"
                                    class="w-full h-full object-cover"
                                />
                                <i v-else class="fas fa-user text-white"></i>
                            </div>
                            <div class="flex-1 overflow-hidden">
                                <div class="name flex items-center text-sm">
                                    <span class="truncate">{{ item.name }}</span>
                                    <span v-if="item._id.$oid === you.id" class="text-green">
                                        ({{ $t('manage_school_users_table_you') }})
                                    </span>
                                </div>

                                <div class="role text-xs truncate">
                                    {{ item.email }}
                                </div>
                            </div>
                        </div>
                        <div class="flex xl:justify-center items-center w-full xl:w-1/5">
                            <div
                                v-if="item.is_school_admin"
                                class="flex justify-center items-center rounded-full bg-purple px-2"
                            >
                                <span class="text-xs text-white">{{ $t('manage_school_users_table_role_admin') }}</span>
                            </div>
                            <div
                                :class="{
                                    'flex justify-center items-center ml-1 rounded-full px-2': true,
                                    'bg-green': ['teacher', 'student'].includes(item.role),
                                    'bg-yellow-dark': ['teacher_plus', 'student_plus'].includes(item.role)
                                }"
                            >
                                <span v-if="item.role === 'teacher'" class="text-xs text-white">
                                    {{ $t('school_role_teacher_basic') }}
                                </span>
                                <span v-if="item.role === 'teacher_plus'" class="text-xs text-white">
                                    {{ $t('school_role_teacher_plus') }}
                                </span>
                                <span v-if="item.role === 'student'" class="text-xs text-white">
                                    {{ $t('school_role_student_basic') }}
                                </span>
                                <span v-if="item.role === 'student_plus'" class="text-xs text-white">
                                    {{ $t('school_role_student_plus') }}
                                </span>
                            </div>
                        </div>
                        <div class="w-full xl:w-1/5">
                            <div
                                :class="{
                                    'text-sm xl:text-center': true
                                }"
                            >
                                <span class="xl:hidden">{{ $t('manage_school_users_table_status') }}: </span>
                                <span
                                    :class="{
                                        'font-bold text-center': true,
                                        'text-red': item.status === 'suspended',
                                        'text-green': item.status === 'active',
                                        'text-grey': !item.status
                                    }"
                                >
                                    <template v-if="item.status === 'suspended'">
                                        {{ $t('manage_school_users_table_suspended') }}
                                    </template>
                                    <template v-else-if="item.status === 'active'">
                                        {{ $t('manage_school_users_table_active') }}
                                    </template>
                                    <template v-else>
                                        {{ $t('manage_school_users_table_unknown') }}
                                    </template>
                                </span>
                            </div>
                        </div>
                        <div class="settings flex justify-end w-full xl:w-1/5">
                            <div class="flex w-full md:w-auto">
                                <XButton
                                    v-tippy="{ placement: 'top' }"
                                    @click="revokeAdmin(item)"
                                    :content="$t('manage_school_settings_table_tippy_revoke')"
                                    :disabled="disableAll || item.revokeLoading"
                                    class="flex-1 ml-auto shadow-text-none"
                                    bg-color="#dc3545"
                                >
                                    <span v-if="item.revokeLoading" class="close-loader"></span>
                                    <i v-else class="fas fa-sign-out-alt"></i>
                                </XButton>
                            </div>
                        </div>
                    </template>
                    <template #empty>
                        <div class="flex justify-center items-center w-full h-full p-4">
                            <div class="flex flex-col justify-center items-center max-w-xl">
                                <i class="fas fa-street-view"></i>
                                <h4
                                    v-html="
                                        keyword
                                            ? $t('manage_school_settings_table_empty_state_keyword', { keyword })
                                            : $t('manage_school_settings_table_empty_state')
                                    "
                                    class="text-center m-0 mt-4"
                                ></h4>
                            </div>
                        </div>
                    </template>
                    <template #loading>
                        <InnerLoader color="#333" class="pin-t pin-l" />
                    </template>
                </ManageTable>
            </div>
        </div>
        <ManageAssignAdmin
            v-if="showManageAssignAdmin"
            @close="showManageAssignAdmin = false"
            @assigned="fetchSchoolAdmins()"
            class="absolute pin-t pin-l w-full z-10"
        />
    </div>
</template>

<script>
import helperMixins from '@/mixins/helper'
import UserService from '@/lib/user'
import popup from '@/lib/popup.js'
import XButton from '@/components/v2/XButton'
import ManageSearch from '@/components/manage-school/ManageSearch'
import ManageTable from '@/components/manage-school/ManageTable'
import ManageAssignAdmin from '@/components/manage-school/ManageAssignAdmin'
import InnerLoader from '@/components/InnerLoader'
import { load } from 'recaptcha-v3'

export default {
    components: {
        XButton,
        ManageSearch,
        ManageTable,
        ManageAssignAdmin,
        InnerLoader
    },

    mixins: [helperMixins],

    data() {
        return {
            schoolName: '',
            schoolSlug: '',
            showManageAssignAdmin: false,
            currentPage: 1,
            pageCount: 0,
            itemsPerPage: 0,
            totalItems: 0,
            keyword: '',
            users: null,
            loading: false,
            disableAll: false,
            invalidSlug: false
        }
    },

    created() {
        this.schoolName = this.you.school_name
        this.schoolSlug = this.you.school_slug
        this.fetchSchoolAdmins()
    },

    computed: {
        you() {
            const userService = new UserService()

            return userService.getUser()
        },

        nuxtPath() {
            return process.env.VUE_APP_NUXT_BASE_URL
        }
    },

    methods: {
        generateSlug() {
            this.schoolSlug = this.slugify(this.schoolName)
        },

        async saveSchool() {
            if (this.disableAll) return

            let errorCount = 0

            const validateResult = await this.$validator.validate()

            if (!validateResult) {
                errorCount++
            }

            if (!this.isValidSlug(this.schoolSlug)) {
                this.invalidSlug = true
                errorCount++
            }

            if (errorCount) return

            this.invalidSlug = false
            this.disableAll = true

            try {
                this.schoolName = this.schoolName.trim().replace(/\s+/g, ' ')

                const res = await this.$store.dispatch('updateSchool', {
                    name: this.schoolName,
                    slug: this.schoolSlug
                })

                const userService = new UserService()

                userService.setToken(res.token)

                this.$store.dispatch('getCredit')
                this.$store.dispatch('findOrderAbility')

                this.toastSuccess(this.$t('manage_school_settings_popup_save_school_complete'))

                const loginSuccessEvent = new CustomEvent('login-success')
                window.dispatchEvent(loginSuccessEvent)
            } catch (err) {
                if (err.response.data.message === 'school_name_exists') {
                    popup.alert(this.$t('manage_school_settings_alert_school_name_exists'))
                } else {
                    this.toastError(this.$t('manage_school_popup_error'))
                }
            } finally {
                this.disableAll = false
            }
        },

        onSearch(keyword) {
            if (this.keyword === keyword) return

            this.fetchSchoolAdmins(keyword)
        },

        async fetchSchoolAdmins(keyword) {
            if (this.keyword === keyword) return

            this.loading = true
            this.disableAll = true

            this.users = null

            const urlParams = new URL(window.location.href)

            if (typeof keyword !== 'undefined') {
                this.keyword = keyword
                this.currentPage = 1

                urlParams.searchParams.delete('page')

                window.history.replaceState(null, null, urlParams.toString())
            }

            this.currentPage = urlParams.searchParams.get('page') || 1

            try {
                const res = await this.$store.dispatch('getUserInSchool', {
                    params: {
                        is_school_admin: 1,
                        keyword: this.keyword,
                        page: this.currentPage
                    }
                })

                this.users = res.data.data.map(user => {
                    return {
                        ...user,
                        revokeLoading: false
                    }
                })

                this.currentPage = res.data.current_page
                this.pageCount = res.data.page_count
                this.itemsPerPage = res.data.perpage
                this.totalItems = res.data.total
            } catch {
                this.toastError(this.$t('manage_school_popup_error'))
            } finally {
                this.loading = false
                this.disableAll = false
            }
        },

        onPageChange(value) {
            this.currentPage = value

            const urlParams = new URL(window.location.href)

            urlParams.searchParams.set('page', this.currentPage)

            window.history.replaceState(null, null, urlParams.toString())

            this.fetchSchoolAdmins()
        },

        revokeAdmin(user) {
            popup.confirm(this.$t('manage_school_settings_confirm_remove_admin'), async () => {
                this.disableAll = true
                user.revokeLoading = true

                try {
                    const recaptcha = await load(process.env.VUE_APP_RECAPTCHA_SITE_KEY, {
                        useRecaptchaNet: true,
                        autoHideBadge: true
                    })

                    const token = await recaptcha.execute('validate_captcha', {
                        action: 'validate_captcha'
                    })

                    await this.$store.dispatch('updateSchoolAdminStatus', {
                        user_id: user._id.$oid,
                        status: 'revoke',
                        captcha_response: token
                    })

                    this.toastSuccess(this.$t('manage_school_settings_popup_remove_admin_complete'))

                    if (user._id.$oid === this.you.id) {
                        location.href = '/my-quiz'
                    } else {
                        this.fetchSchoolAdmins()
                    }
                } catch {
                    this.toastError(this.$t('manage_school_popup_error'))
                    user.revokeLoading = true
                } finally {
                    this.disableAll = false
                }
            })
        },

        openSchool() {
            window.open(
                `${this.nuxtPath}/${this.you.provider === 'feide' ? 'skoler' : 'school'}/${this.schoolSlug}`,
                '_blank'
            )
        }
    }
}
</script>

<style lang="scss">
#manage-settings {
    row-gap: 1rem;

    #settings-section {
        row-gap: 1.5rem;

        .settings-school-name-section {
            column-gap: 0.5rem;
            row-gap: 1rem;

            .settings-school-name-form {
                column-gap: 0.5rem;
                row-gap: 1rem;
            }

            .save-button-box {
                @media screen and (min-width: 768px) {
                    height: 72px;
                }

                button {
                    border-radius: 0.25rem !important;
                }
            }
        }

        .form-control {
            row-gap: 7px;

            label {
                font-size: 14px;
                font-weight: bold;
            }

            input {
                height: 40px;
                border-radius: 0.25rem;
                padding: 10.5px;
                border: 1px solid #ced4da;
                font-size: 14px;
                color: #495057;
            }
        }

        .school-url-input {
            border-radius: 0.25rem 0 0 0.25rem !important;
        }

        .school-url-button {
            border-radius: 0 0.25rem 0.25rem 0 !important;
        }

        .setting-admin-section {
            row-gap: 1rem;

            .manage-table-item {
                row-gap: 1rem;

                .user-detail {
                    column-gap: 1rem;

                    .avatar {
                        width: 2rem;
                        height: auto !important;
                        aspect-ratio: 1 / 1;
                        background-color: gray;
                    }

                    .name {
                        column-gap: 0.3rem;
                    }
                }

                .settings {
                    > div {
                        column-gap: 0.5rem;

                        button {
                            width: 100%;
                            height: 2rem;

                            @media screen and (min-width: 768px) {
                                width: 2rem;
                                height: auto;
                                padding: 0;
                                aspect-ratio: 1 / 1;
                            }
                        }
                    }
                }
            }
        }
    }
}
</style>
